import { useState } from "react";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { merch, whatsappLink, collagePhotos } from "../data/content";
import Reveal from "../components/site/Reveal";
import NewsletterSignup from "../components/site/NewsletterSignup";

const ProductCard = ({ product, index }) => {
    const [view, setView] = useState(0);
    const current = product.images[view];

    return (
        <Reveal delay={index * 0.12}>
            <article data-testid={`merch-card-${index}`} className="group h-full flex flex-col">
                <div className="relative border border-line bg-white p-4 shadow-md group-hover:shadow-2xl transition-shadow duration-500">
                    <span className="absolute top-6 left-6 z-10 bg-ink text-paper font-mono text-[10px] tracking-[0.2em] px-2 py-1">
                        {current.label}
                    </span>
                    <div className="aspect-[3/4] overflow-hidden bg-[#f2f2f0]">
                        <img
                            src={current.src}
                            alt={`${product.name} — ${current.label}`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                    </div>
                    {product.images.length > 1 && (
                        <div className="mt-4 flex gap-2">
                            {product.images.map((img, i) => (
                                <button
                                    key={img.label}
                                    data-testid={`merch-view-${img.label.toLowerCase()}-${index}`}
                                    onClick={() => setView(i)}
                                    className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                                        view === i
                                            ? "bg-ink text-paper border-ink"
                                            : "border-line text-subtle hover:border-ink hover:text-ink"
                                    }`}
                                >
                                    {img.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="pt-5 flex-1 flex flex-col">
                    <div className="flex items-baseline justify-between gap-3">
                        <h3 className="font-display text-2xl leading-tight text-ink">{product.name}</h3>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson shrink-0">
                            {product.color}
                        </span>
                    </div>
                    <p className="mt-3 font-display italic text-lg leading-snug text-ink/70 flex-1">
                        «{product.verse}»
                    </p>
                    <p
                        data-testid={`merch-price-note-${index}`}
                        className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle"
                    >
                        Precio y tallas: consultar por WhatsApp
                    </p>
                    <a
                        data-testid={`merch-buy-button-${index}`}
                        href={whatsappLink(product.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-5 inline-flex items-center justify-center gap-3 bg-ink text-paper px-6 py-4 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-[#1FA855] transition-colors duration-300"
                    >
                        <MessageCircle size={15} />
                        Comprar en WhatsApp
                    </a>
                </div>
            </article>
        </Reveal>
    );
};

const VersosParaLlevar = () => (
    <main data-testid="versos-para-llevar" className="min-h-screen bg-paper text-ink">
        <header className="border-b border-line">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
                <span className="font-display text-xl sm:text-2xl tracking-tight">
                    José Luis <span className="italic text-crimson">Garcés Maldonado</span>
                </span>
                <a
                    data-testid="merch-back-link"
                    href="/"
                    className="link-underline flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-subtle hover:text-ink transition-colors"
                >
                    <ArrowLeft size={14} />
                    Volver a la web
                </a>
            </div>
        </header>

        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-10">
            <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-crimson">
                    Tienda · Merchandising
                </p>
            </Reveal>
            <Reveal delay={0.1}>
                <h1 className="mt-4 font-display font-light tracking-tight leading-[1.02] text-5xl sm:text-7xl">
                    Versos para <span className="italic text-crimson">llevar</span>
                </h1>
            </Reveal>
            <Reveal delay={0.2}>
                <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-ink/75">
                    Hacer de la poesía esa piel que deja las hojas y vive en las
                    calles. Prendas que mezclan palabra e ilustración, y que
                    identifican a quien las lleva.
                </p>
            </Reveal>
        </section>

        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {merch.map((product, i) => (
                <ProductCard key={product.id} product={product} index={i} />
            ))}
        </section>

        <section
            data-testid="merch-collage-section"
            className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32"
        >
            <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-crimson">
                    La poesía en la calle
                </p>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="mt-4 font-display font-light tracking-tight text-3xl sm:text-5xl">
                    Así se ven <span className="italic text-crimson">puestas</span>
                </h2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-10 lg:gap-16 items-start">
                {collagePhotos.map((photo, i) => (
                    <Reveal key={photo.src} delay={0.12 + i * 0.1}>
                        <figure
                            data-testid={`merch-collage-photo-${i}`}
                            className={`border border-line bg-white p-3 pb-4 shadow-xl hover:rotate-0 transition-transform duration-500 ${
                                i % 2 === 0 ? "rotate-[-1.5deg]" : "rotate-[1.5deg] sm:mt-20"
                            }`}
                        >
                            <img
                                src={photo.src}
                                alt={photo.alt}
                                className="w-full aspect-[3/4] object-cover"
                            />
                            <figcaption className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                                {photo.caption}
                            </figcaption>
                        </figure>
                    </Reveal>
                ))}
            </div>
        </section>

        <footer className="border-t border-line bg-cream">
            <NewsletterSignup />
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-subtle">
                    Escríbeme por WhatsApp para tallas, precios y entregas · Quito, Ecuador
                </p>
                <img
                    src="/media/trazo-firma.png"
                    alt="Firma de José Luis Garcés Maldonado"
                    className="h-10 w-auto opacity-70"
                />
            </div>
        </footer>
    </main>
);

export default VersosParaLlevar;
